/**
 * Fathom Analytics Event Tracking Configuration
 *
 * Event Naming Convention: gradlecom_page_section_element_action
 *
 * - prefix: Always starts with 'gradlecom_'
 * - page: The page or main section (e.g., 'scans', 'pricing', 'trial')
 * - section: The specific section within the page (e.g., 'hero', 'how-to', 'features')
 * - element: The UI element being interacted with (e.g., 'button', 'link', 'icon')
 * - action: The action being performed (e.g., 'click', 'copy', 'submit')
 *
 * Example: 'gradlecom_scans_how-to_gradle_icon_click'
 *
 * To add a new event:
 * 1. Add the event to the appropriate page object below
 * 2. Use the typed event name in your component with trackFathomEvent function
 * 3. Follow the naming convention strictly for consistency
 */

// Relative + `.ts` (not `@/`): tools/revenue-pages imports this module under plain Node.
import { PRODUCT_INTEREST_NAMES, type ProductInterestSlug } from '../forms/product-interest-names.generated.ts';

// Recursive type to allow flexible nesting depth for event objects
type FathomEventLeaf = string;
type FathomEventNode = { [key: string]: FathomEventLeaf | FathomEventNode };
type FathomEventStructure = { [page: string]: FathomEventNode };

/** CTA families whose clicks are attributed per product slug (`?product=<slug>`). */
type ProductCtaPage = 'demo' | 'trial' | 'contact' | 'roadmap' | 'scans';

/** A per-product CTA map: the slug-less `default` plus one click event per product slug.
 * Keyed by the exact generated slug union, so event names stay literal types and an
 * unknown slug / typo'd name is a compile error. */
export type ProductCtaEvents<P extends ProductCtaPage, S extends string = ProductInterestSlug> = {
  default: { click: `gradlecom_${P}_cta_click` };
} & { [K in S]: { click: `gradlecom_${P}_cta_${K}_click` } };

const PRODUCT_INTEREST_SLUGS = Object.keys(PRODUCT_INTEREST_NAMES) as ProductInterestSlug[];

/** Build a per-product CTA map: `default` → `gradlecom_<page>_cta_click`, and each slug →
 * `gradlecom_<page>_cta_<slug>_click`. Derived (not hand-listed) from the generated
 * PRODUCT_INTEREST_NAMES slug whitelist with a fixed template, so a `pnpm sync:specs` that
 * adds a product page gets its CTA events with no hand edit. This is a structural lookup
 * off trusted slugs, never derived from copy, so the co-location rule still holds.
 * (NoInfer on the return: inside `FATHOM_EVENTS … satisfies FathomEventStructure` the
 * contextual index signature would otherwise infer S = string and widen every name.) */
export function productCtaEvents<P extends ProductCtaPage, S extends string = ProductInterestSlug>(
  page: P,
  slugs: readonly S[] = PRODUCT_INTEREST_SLUGS as readonly string[] as readonly S[],
): ProductCtaEvents<P, NoInfer<S>> {
  return {
    ...Object.fromEntries(slugs.map(slug => [slug, { click: `gradlecom_${page}_cta_${slug}_click` }])),
    default: { click: `gradlecom_${page}_cta_click` },
  } as ProductCtaEvents<P, S>;
}

export const FATHOM_EVENTS = {
  scans: {
    'how-to': {
      gradle_icon: {
        click: 'gradlecom_scans_how-to_gradle_icon_click',
      },
      maven_icon: {
        click: 'gradlecom_scans_how-to_maven_icon_click',
      },
      sbt_icon: {
        click: 'gradlecom_scans_how-to_sbt_icon_click',
      },
      instructions: {
        copy: 'gradlecom_scans_how-to_instructions_copy',
        click: 'gradlecom_scans_how-to_instructions_click',
      },
      quickstart_link: {
        click: 'gradlecom_scans_how-to_quickstart_link_click',
      },
    },
    // Per-product "Try … Free" / "Start Free Trial" CTA clicks that point at the free
    // Build Scan publish page (`/scans/`) instead of a conversion page. build-scan is the
    // only product page whose primary trial CTAs link to `/scans/` today; resolve.ts
    // deep-links a bare `/scans/` CTA on a known product page to `/scans/?product=<slug>`
    // (the /scans page ignores the inert param) so the NextLinkAdapter seam can attribute
    // the click by slug via scansCtaFathomEvent(slug) — a whitelist lookup, NEVER derived
    // from copy. Keys are derived from PRODUCT_INTEREST_NAMES (productCtaEvents), exactly
    // like trial.cta. `default` covers a `/scans/` CTA with no product slug. Note `/scans/`
    // 301s to `/scans/gradle/` (next.config.js), so the click lands on
    // `/scans/gradle/?product=<slug>` — harmless:
    // the event fires on click before the redirect, and Fathom keys pageviews on the
    // (query-stripped) pathname, so the redirect neither drops the event nor double-counts.
    cta: productCtaEvents('scans'),
  },
  resources: {
    'ebooks-and-whitepapers': {
      gen_ai_form: {
        submit: 'gradlecom_resources_ebooks-and-whitepapers_gen-ai_form_submit',
      },
      flaky_tests_form: {
        submit: 'gradlecom_resources_ebooks-and-whitepapers_flaky-tests_form_submit',
      },
      // AI-native SDLC closing band: "Download PDF" and "Request a demo" (/demo/).
      ai_native_sdlc_cta: {
        demo: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_cta-demo_click' },
        download_pdf: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_cta-download-pdf_click' },
      },
      // The online edition (ungated reader). Tracks the hero, the contents, sharing, figures,
      // notes and citations, and how far people read: depth milestones (share of the paper) and
      // each section reached (after a few seconds there, so jumps don't count), once per page view. `section` is keyed by the chapters' ids
      // (whitepaper/a-model-of-the-ai-native-sdlc.md frontmatter); a unit test keeps the two in step.
      ai_native_sdlc_reader: {
        start_reading: {
          click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-start-reading_click',
        },
        cover: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-cover_click' },
        download_pdf: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-download-pdf_click' },
        contents: {
          click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-contents_click',
          subsection: {
            click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-contents-subsection_click',
          },
          // The phone/tablet reading bar's drop-down: opening it, and a chapter picked from it.
          open: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-contents_open',
          sheet: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-contents-sheet_click' },
        },
        // Expand, per figure: keyed like `section` (figures.ts figureKey; a unit test keeps them in step).
        figure: {
          overview: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-figure-overview_open',
          'figure-2-stages': 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-figure-2-stages_open',
          'figure-3-verification-pyramid':
            'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-figure-3-verification-pyramid_open',
          'figure-4-inside-coding':
            'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-figure-4-inside-coding_open',
        },
        note: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-note_click' },
        // Links in the text: to outside sources, and to Develocity's own pages (blog posts).
        citation: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-citation_click' },
        site_link: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-site-link_click' },
        share: {
          hero: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-share-hero_click' },
          contents: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-share-contents_click' },
          bar: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-share-bar_click' },
          // The foot of the reading bar's drop-down contents.
          sheet: { click: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-share-sheet_click' },
          // Outcome, whichever button: copied the link, or opened the system share sheet.
          copied: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-share_copied',
          native: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-share_native',
        },
        depth: {
          25: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-depth-25_reach',
          50: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-depth-50_reach',
          75: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-depth-75_reach',
          100: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-depth-100_reach',
        },
        section: {
          'executive-summary':
            'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-executive-summary_reach',
          introduction: 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-introduction_reach',
          'ch-01': 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-ch-01_reach',
          'ch-02': 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-ch-02_reach',
          'ch-03': 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-ch-03_reach',
          'ch-04': 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-ch-04_reach',
          'ch-05': 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-ch-05_reach',
          'ch-06': 'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-ch-06_reach',
          acknowledgments:
            'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-acknowledgments_reach',
          'about-the-author':
            'gradlecom_resources_ebooks-and-whitepapers_ai-native-sdlc_reader-section-about-the-author_reach',
        },
      },
    },
  },
  blog: {
    index: {
      card: {
        click: 'gradlecom_blog_index_card_click',
      },
      subscribe: {
        submit: 'gradlecom_blog_index_subscribe_submit',
      },
      legacy_card: {
        click: 'gradlecom_blog_index_legacy-card_click',
      },
    },
    post: {
      page: {
        view: 'gradlecom_blog_post_page_view',
      },
      code_block: {
        copy: 'gradlecom_blog_post_code-block_copy',
      },
      header: {
        copy: 'gradlecom_blog_post_header_copy',
      },
      image: {
        open: 'gradlecom_blog_post_image_open',
      },
      sidebar: {
        cta_click: 'gradlecom_blog_post_sidebar_cta-click',
      },
      mobile_actions: {
        cta_click: 'gradlecom_blog_post_mobile-actions_cta-click',
      },
    },
  },
  'review-terms': {
    hero: {
      form: {
        submit: 'gradlecom_review-terms_hero_form_submit',
      },
    },
  },
  develocity: {
    overview: {
      hero: {
        request_a_trial_click: 'gradlecom_develocity_overview_hero_request-a-trial-click',
        learn_more_click: 'gradlecom_develocity_overview_hero_learn-more-click',
      },
      'trial-request': {
        request_a_trial_click: 'gradlecom_develocity_overview_trial-request_request-a-trial-click',
      },
      'need-help': {
        read_documentation_click: 'gradlecom_develocity_overview_need-help_read-documentation-click',
      },
      acceleration: {
        cta_click: 'gradlecom_develocity_overview_acceleration_cta-click',
      },
      governance: {
        cta_click: 'gradlecom_develocity_overview_governance_cta-click',
      },
      existingTech: {
        learn_more_click: 'gradlecom_develocity_overview_existing-tech_learn-more-click',
      },
      testimonials: {
        cta_click: 'gradlecom_develocity_overview_testimonials_cta-click',
      },
      improveSpeed: {
        learn_more_click: 'gradlecom_develocity_overview_improve-speed_learn-more-click',
        request_trial_click: 'gradlecom_develocity_overview_improve-speed_request-trial-click',
      },
      'develocity-360': {
        unified_visibility_click: 'gradlecom_develocity_overview_360_unified-visibility_click',
        agentic_ai_click: 'gradlecom_develocity_overview_360_agentic-ai_click',
        contextual_data_click: 'gradlecom_develocity_overview_360_contextual-data_click',
        accessible_format_click: 'gradlecom_develocity_overview_360_accessible-format_click',
        pipeline_coverage_click: 'gradlecom_develocity_overview_360_pipeline-coverage_click',
        troubleshooting_support_click: 'gradlecom_develocity_overview_360_troubleshooting-support_click',
        learn_more_click: 'gradlecom_develocity_overview_360_learn-more_click',
      },
      acceleration_tabs: {
        uc_click: 'gradlecom_develocity_overview_acceleration-tabs_uc_click',
        pts_click: 'gradlecom_develocity_overview_acceleration-tabs_pts_click',
        td_click: 'gradlecom_develocity_overview_acceleration-tabs_td_click',
      },
      observe: {
        cta_click: 'gradlecom_develocity_overview_observe_cta-click',
      },
      observe_tabs: {
        build_scan_click: 'gradlecom_develocity_overview_observe-tabs_build-scan_click',
        analytics_click: 'gradlecom_develocity_overview_observe-tabs_analytics_click',
        performance_insights_click: 'gradlecom_develocity_overview_observe-tabs_performance-insights_click',
        flaky_test_click: 'gradlecom_develocity_overview_observe-tabs_flaky-test_click',
        failure_analytics_click: 'gradlecom_develocity_overview_observe-tabs_failure-analytics_click',
      },
      governance_tabs: {
        provenance_click: 'gradlecom_develocity_overview_governance-tabs_provenance_click',
        policy_click: 'gradlecom_develocity_overview_governance-tabs_policy_click',
      },
      faqs: {
        toolchain_observability_click: 'gradlecom_develocity_overview_faqs_toolchain-observability_click',
        track_dora_click: 'gradlecom_develocity_overview_faqs_track-dora_click',
        builds_tests_faster_click: 'gradlecom_develocity_overview_faqs_builds-tests-faster_click',
        troubleshoot_click: 'gradlecom_develocity_overview_faqs_troubleshoot_click',
        governance_compliance_click: 'gradlecom_develocity_overview_faqs_governance-compliance_click',
      },
    },
    'develocity-360': {
      agentic_ai: {
        video_clicked: 'gradlecom_develocity-360-agentic-ai-video-clicked',
      },
      contact_us: {
        submit: 'gradlecom_develocity-360-contact-us-form-submit',
      },
      request_more_info: {
        click: 'gradlecom_develocity-360-request-more-info-click',
      },
      need_help: {
        read_documentation_click: 'gradlecom_develocity-360-need-help-read-documentation-click',
      },
    },
    analytics: {
      hero: {
        video_clicked: 'gradlecom_analytics_hero_video-clicked',
      },
      'post-hero': {
        tour_the_product_clicked: 'gradlecom_analytics_post-hero_tour-the-product-click',
      },
      'benefits-tabs': {
        individuals_tab_click: 'gradlecom_analytics_benefits-tabs_individuals-click',
        teams_tab_click: 'gradlecom_analytics_benefits-tabs_teams-click',
        organizations_tab_click: 'gradlecom_analytics_benefits-tabs_organizations-click',
      },
      'learn-more': {
        read_blog_post_click: 'gradlecom_analytics_learn-more_read-blog-post-click',
        watch_video_click: 'gradlecom_analytics_learn-more_watch-video-click',
        learn_more_click: 'gradlecom_analytics_learn-more_learn-more-click',
      },
    },
    'performance-insights': {
      'post-hero': {
        tour_the_product_clicked: 'gradlecom_performance-insights_post-hero_tour-the-product-click',
      },
      'learn-more': {
        take_the_tour_click: 'gradlecom_performance-insights_learn-more_take-the-tour-click',
        explore_the_data_click: 'gradlecom_performance-insights_learn-more_explore-the-data-click',
        take_the_course_click: 'gradlecom_performance-insights_learn-more_take-the-course-click',
      },
      'need-help': {
        read_documentation_click: 'gradlecom_performance-insights_need-help_read-documentation-click',
      },
    },
    'universal-cache': {
      hero: {
        cta_click: 'gradlecom_universal-cache_hero_cta-click',
      },
      'learn-more': {
        blog_post_click: 'gradlecom_universal-cache_learn-more_blog-post-click',
        watch_video_click: 'gradlecom_universal-cache_learn-more_watch-video-click',
        learn_more_click: 'gradlecom_universal-cache_learn-more_learn-more-about-360-click',
      },
      'need-help': {
        read_documentation_click: 'gradlecom_universal-cache_need-help_read-documentation-click',
      },
      contact: {
        form_submit: 'gradlecom_develocity_universal-cache_contact_form-submit',
      },
    },
    'provenance-governor': {
      hero: {
        learn_more_button_click: 'gradlecom_develocity_provenance-governor_hero_learn-more-button_click',
        get_in_touch_button_click: 'gradlecom_develocity_provenance-governor_hero_get-in-touch-button_click',
      },
      contact_us: {
        submit: 'gradlecom_provenance-governor-contact-us-form-submit',
      },
    },
    saas: {
      contact: {
        form_submit: 'gradlecom_develocity_saas_contact_form-submit',
      },
    },
    observe: {
      hero: {
        cta_clicked: 'gradlecom_develocity_observe_hero_cta-clicked',
      },
      features: {
        performance_insights_cta_click: 'gradlecom_develocity_observe_features_performance-insights-cta-click',
        analytics_cta_click: 'gradlecom_develocity_observe_features_analytics-cta-click',
      },
      testimonial: {
        watch_video_click: 'gradlecom_develocity_observe_testimonial_watch-video-click',
      },
    },
    troubleshoot: {
      hero: {
        cta_clicked: 'gradlecom_develocity_troubleshoot_hero_cta-clicked',
      },
      features: {
        build_scan_cta_click: 'gradlecom_develocity_troubleshoot_features_build-scan-cta-click',
        flaky_test_detection_cta_click: 'gradlecom_develocity_troubleshoot_features_flaky-test-detection-cta-click',
        failure_analytics_cta_click: 'gradlecom_develocity_troubleshoot_features_failure-analytics-cta-click',
      },
    },
    accelerate: {
      hero: {
        cta_clicked: 'gradlecom_develocity_accelerate_hero_cta-clicked',
      },
      features: {
        build_cache_cta_click: 'gradlecom_develocity_accelerate_features_build-cache-cta-click',
        predictive_test_selection_cta_click:
          'gradlecom_develocity_accelerate_features_predictive-test-selection-cta-click',
        test_distribution_cta_click: 'gradlecom_develocity_accelerate_features_test-distribution-cta-click',
      },
      testimonial: {
        get_the_story: 'gradlecom_develocity_accelerate_testimonial_get-the-story',
      },
    },
    'contact-us': {
      hero: {
        // Fires on submit ATTEMPT (after the client email guard) — counts retries and
        // server rejections. Keep the typo'd name; it's the live event. See README.
        form_submited: 'gradlecom_develocity_contact-us_hero_form-submited',
        // Fires ONCE on the confirmed success screen (?success=true) — the true
        // conversion. Hyphen matches the neighbour above.
        form_success: 'gradlecom_develocity_contact-us_hero_form-success',
        // Work-email gate monitoring (keep the gate, measure it). See README "Work-email gate".
        work_email: {
          // Client gate stopped a submit (fires on every blocked click).
          blocked: 'gradlecom_develocity_contact-us_hero_work-email_blocked',
          // Inline "use your work email" hint first rendered (once per ContactForm mount).
          warning_shown: 'gradlecom_develocity_contact-us_hero_work-email_warning-shown',
          // A submit that passed the gate after the hint was shown (once per mount).
          // warning-shown − this ≈ drop-off after seeing the hint.
          submitted_after_warning: 'gradlecom_develocity_contact-us_hero_work-email_submitted-after-warning',
          // "Run a free Build Scan" link shown after a blocked submit was clicked.
          build_scan_click: 'gradlecom_develocity_contact-us_hero_work-email_build-scan-click',
          // Server rejection card rendered (?error=work-email-required), keyed by the
          // server's `?reason=` — a structural lookup, never derived from copy.
          rejected: {
            free: 'gradlecom_develocity_contact-us_hero_work-email_rejected-free',
            disposable: 'gradlecom_develocity_contact-us_hero_work-email_rejected-disposable',
            invalid: 'gradlecom_develocity_contact-us_hero_work-email_rejected-invalid',
          },
        },
        // Form funnel + breakage monitoring (see README "Form funnel"). `started` = first real
        // input (not focus), once per form mount. `error` fires when the server's error card
        // renders, keyed by the `?error=` status — a structural lookup, never copy. `turnstile_error`
        // = the security check failed in the browser (once per mount, production hosts only).
        funnel: {
          started: 'gradlecom_develocity_contact-us_hero_form_started',
          turnstile_error: 'gradlecom_develocity_contact-us_hero_turnstile_error',
          error: {
            'verification-required': 'gradlecom_develocity_contact-us_hero_form_error-verification-required',
            'verification-failed': 'gradlecom_develocity_contact-us_hero_form_error-verification-failed',
            'submission-failed': 'gradlecom_develocity_contact-us_hero_form_error-submission-failed',
          },
        },
      },
    },
  },
  home: {
    hero: {
      see_platform_video: { open: 'gradlecom_home_hero_see-platform-video_open' },
    },
    'whats-new': {
      latest_release: { click: 'gradlecom_home_whats-new_latest-release-card_click' },
      latest_release_pill: { click: 'gradlecom_home_whats-new_latest-release-pill_click' },
      build_caching_optimizer: { click: 'gradlecom_home_whats-new_build-caching-optimizer-card_click' },
      pts_anthropic: { click: 'gradlecom_home_whats-new_pts-anthropic-card_click' },
      regression_tests: { click: 'gradlecom_home_whats-new_regression-tests-card_click' },
      // Card for the AI-native SDLC whitepaper; only in the grid once it's published.
      ai_native_sdlc: { click: 'gradlecom_home_whats-new_ai-native-sdlc-card_click' },
    },
  },
  trial: {
    hero: {
      form: {
        // The trial hero form's confirmed-success event — fires once on the success
        // screen. The submit ATTEMPT still fires the legacy `gradlecom_trial-request`
        // (LEGACY_FATHOM_EVENTS.TRIAL_REQUEST); that name is kept. See README.
        success: 'gradlecom_trial_hero_form_success',
      },
      // Work-email gate monitoring (keep the gate, measure it). See README "Work-email gate".
      work_email: {
        // Client gate stopped a submit (fires on every blocked click).
        blocked: 'gradlecom_trial_hero_work-email_blocked',
        // Inline "use your work email" hint first rendered (once per ContactForm mount).
        warning_shown: 'gradlecom_trial_hero_work-email_warning-shown',
        // A submit that passed the gate after the hint was shown (once per mount).
        // warning-shown − this ≈ drop-off after seeing the hint.
        submitted_after_warning: 'gradlecom_trial_hero_work-email_submitted-after-warning',
        // "Run a free Build Scan" link shown after a blocked submit was clicked.
        build_scan_click: 'gradlecom_trial_hero_work-email_build-scan-click',
        // Server rejection card rendered (?error=work-email-required), keyed by the
        // server's `?reason=` — a structural lookup, never derived from copy.
        rejected: {
          free: 'gradlecom_trial_hero_work-email_rejected-free',
          disposable: 'gradlecom_trial_hero_work-email_rejected-disposable',
          invalid: 'gradlecom_trial_hero_work-email_rejected-invalid',
        },
      },
      // Form funnel + breakage monitoring (see README "Form funnel"). `started` = first real
      // input (not focus), once per form mount. `error` fires when the server's error card
      // renders, keyed by the `?error=` status — a structural lookup, never copy. `turnstile_error`
      // = the security check failed in the browser (once per mount, production hosts only).
      funnel: {
        started: 'gradlecom_trial_hero_form_started',
        turnstile_error: 'gradlecom_trial_hero_turnstile_error',
        error: {
          'verification-required': 'gradlecom_trial_hero_form_error-verification-required',
          'verification-failed': 'gradlecom_trial_hero_form_error-verification-failed',
          'submission-failed': 'gradlecom_trial_hero_form_error-submission-failed',
        },
      },
    },
    // Per-product "Start free trial" CTA clicks on product/solution pages (Part B).
    // These CTAs point at `/trial/?product=<slug>` (deep-linked in resolve.ts) and render
    // through the NextLinkAdapter link seam (adapters.tsx), so the click is attributed by
    // slug via trialCtaFathomEvent(slug) — a whitelist lookup, NEVER derived from copy.
    // Keys are derived from PRODUCT_INTEREST_NAMES (productCtaEvents), exactly like demo.cta
    // below. `default` covers a trial CTA with no product slug.
    cta: productCtaEvents('trial'),
    'how-it-works': {
      install: { click: 'gradlecom_trial_how-it-works_install_click' },
      connect: { click: 'gradlecom_trial_how-it-works_connect_click' },
      capture: { click: 'gradlecom_trial_how-it-works_capture_click' },
      optimize: { click: 'gradlecom_trial_how-it-works_optimize_click' },
      quantify: { click: 'gradlecom_trial_how-it-works_quantify_click' },
      present: { click: 'gradlecom_trial_how-it-works_present_click' },
    },
    'case-studies': {
      tile: { click: 'gradlecom_trial_case-studies_tile_click' },
      'read-cta': { click: 'gradlecom_trial_case-studies_read-cta_click' },
    },
    faq: {
      process: { click: 'gradlecom_trial_faq_process_click' },
      'how-long': { click: 'gradlecom_trial_faq_how-long_click' },
      'get-at-end': { click: 'gradlecom_trial_faq_get-at-end_click' },
      'install-develocity': { click: 'gradlecom_trial_faq_install-develocity_click' },
      'hosted-installation': { click: 'gradlecom_trial_faq_hosted-installation_click' },
      license: { click: 'gradlecom_trial_faq_license_click' },
    },
  },
  solutions: {
    'generative-ai': {
      hero: {
        get_whitepaper_clicked: 'gradlecom_solutions_generative-ai_hero_get_whitepaper_clicked',
      },
      acceleration_overview: {
        pts_learn_more_button_click:
          'gradlecom_solutions_generative-ai_acceleration-overview_pts-learn-more-button_click',
        build_cache_learn_more_button_click:
          'gradlecom_solutions_generative-ai_acceleration-overview_build-cache-learn-more-button_click',
        td_learn_more_button_click:
          'gradlecom_solutions_generative-ai_acceleration-overview_td-learn-more-button_click',
      },
      troubleshooting_overview: {
        build_scan_learn_more_button_click:
          'gradlecom_solutions_generative-ai_troubleshooting-overview_build-scan-learn-more-button_click',
        failure_clustering_analytics_learn_more_button_click:
          'gradlecom_solutions_generative-ai_troubleshooting-overview_failure-clustering-analytics-learn-more-button_click',
        flaky_test_detection_learn_more_button_click:
          'gradlecom_solutions_generative-ai_troubleshooting-overview_flaky-test-detection-learn-more-button_click',
      },
      governance_overview: {
        request_more_info_button_click:
          'gradlecom_solutions_generative-ai_governance-overview_request-more-info-button_click',
      },
      observability_overview: {
        learn_more_button_click: 'gradlecom_solutions_generative-ai_observability-overview_learn-more-button_click',
      },
    },
    dora: {
      hero: {
        learn_more_clicked: 'gradlecom_solutions_dora_hero_learn-more_clicked',
      },
      'dora-significance': {
        smaller_batches_text: 'gradlecom_solutions_dora_dora-significance_smaller-batches_clicked',
        continuous_testing_text: 'gradlecom_solutions_dora_dora-significance_continuous-testing_clicked',
        netflix_text: 'gradlecom_solutions_dora_dora-significance_netflix-text_clicked',
        netflix_text_2: 'gradlecom_solutions_dora_dora-significance_netflix-text-2_clicked',
        spotify_text: 'gradlecom_solutions_dora_dora-significance_spotify-text_clicked',
        revolut_text: 'gradlecom_solutions_dora_dora-significance_revolut-text_clicked',
        applied_materials_text: 'gradlecom_solutions_dora_dora-significance_applied-materials-text_clicked',
      },
      'related-features': {
        build_cache_card: 'gradlecom_solutions_dora_related-features_build-cache-card_clicked',
        predictive_test_selection_card:
          'gradlecom_solutions_dora_related-features_predictive-test-selection-card_clicked',
        test_distribution_card: 'gradlecom_solutions_dora_related-features_test-distribution-card_clicked',
        build_scan_card: 'gradlecom_solutions_dora_related-features_build-scan-card_clicked',
        td_card: 'gradlecom_solutions_dora_related-features_td-card_clicked',
        failure_analytics_card: 'gradlecom_solutions_dora_related-features_failure-analytics-card_clicked',
        failure_summaries_card: 'gradlecom_solutions_dora_related-features_failure-summaries-card_clicked',
        flaky_test_detection_card: 'gradlecom_solutions_dora_related-features_flaky-test-detection-card_clicked',
        analytics_card: 'gradlecom_solutions_dora_related-features_analytics-card_clicked',
        software_provenance_card: 'gradlecom_solutions_dora_related-features_software-provenance-card_clicked',
        policy_automation_card: 'gradlecom_solutions_dora_related-features_policy-automation-card_clicked',
        ci_build_system_card: 'gradlecom_solutions_dora_related-features_ci-build-system-card_clicked',
        analytics_card_2: 'gradlecom_solutions_dora_related-features_analytics-card-2_clicked',
        performance_insights_card: 'gradlecom_solutions_dora_related-features_performance-insights-card_clicked',
        analytics_card_3: 'gradlecom_solutions_dora_related-features_analytics-card-3_clicked',
      },
    },
  },
  'security-advisories': {
    index: {
      advisory_row: {
        click: 'gradlecom_security-advisories_index_advisory-row_click',
      },
      github_link: {
        click: 'gradlecom_security-advisories_index_github-link_click',
      },
    },
  },
  // Site-wide announcement bar (src/components/Banner). The rebrand campaign keeps the legacy
  // MARKETING_BANNER_LINK_CLICK event.
  banner: {
    ai_native_sdlc: { click: 'gradlecom_site_announcement-banner_ai-native-sdlc-link_click' },
  },
  header: {
    nav: {
      gradle_logo: { click: 'gradlecom_header_nav_gradle-logo_click' },
      pricing: { click: 'gradlecom_header_nav_pricing_click' },
      customers: { click: 'gradlecom_header_nav_customers_click' },
      request_trial: { click: 'gradlecom_header_nav_request-trial_click' },
      products: {
        develocity: { click: 'gradlecom_header_nav_products_develocity_click' },
        build_tool: { click: 'gradlecom_header_nav_products_build-tool_click' },
      },
      features: {
        develocity_360: { click: 'gradlecom_header_nav_features_develocity-360_click' },
        pi: { click: 'gradlecom_header_nav_features_pi_click' },
        uc: { click: 'gradlecom_header_nav_features_uc_click' },
        pts: { click: 'gradlecom_header_nav_features_pts_click' },
        td: { click: 'gradlecom_header_nav_features_td_click' },
        build_scan: { click: 'gradlecom_header_nav_features_build-scan_click' },
        flaky_test: { click: 'gradlecom_header_nav_features_flaky-test_click' },
        failure_analytics: { click: 'gradlecom_header_nav_features_failure-analytics_click' },
        security: { click: 'gradlecom_header_nav_features_security_click' },
        provenance_governor: { click: 'gradlecom_header_nav_features_provenance-governor_click' },
        reporting_visualization: { click: 'gradlecom_header_nav_features_reporting-visualization_click' },
      },
      solutions: {
        use_cases: {
          genAI: { click: 'gradlecom_header_nav_solutions_use-cases_genAI_click' },
          dora_cd: { click: 'gradlecom_header_nav_solutions_use-cases_dora-cd_click' },
        },
        build_sys: {
          gbt: { click: 'gradlecom_header_nav_solutions_build-sys_gbt_click' },
          android: { click: 'gradlecom_header_nav_solutions_build-sys_android_click' },
          maven: { click: 'gradlecom_header_nav_solutions_build-sys_maven_click' },
          sbt: { click: 'gradlecom_header_nav_solutions_build-sys_sbt_click' },
        },
        integrations: {
          ci_systems: { click: 'gradlecom_header_nav_solutions_integrations_ci-systems_click' },
          languages: { click: 'gradlecom_header_nav_solutions_integrations_languages_click' },
          ides: { click: 'gradlecom_header_nav_solutions_integrations_ides_click' },
          test_frameworks: { click: 'gradlecom_header_nav_solutions_integrations_test-frameworks_click' },
          cloud_infra: { click: 'gradlecom_header_nav_solutions_integrations_cloud-infra_click' },
          apis_data_reporting: { click: 'gradlecom_header_nav_solutions_integrations_apis-data-reporting_click' },
          build_systems_package_managers: {
            click: 'gradlecom_header_nav_solutions_integrations_build-systems-package-managers_click',
          },
        },
      },
      resources: {
        learning_center: {
          dpeu: { click: 'gradlecom_header_nav_resources_learning-center_dpeu_click' },
          events_webinars: { click: 'gradlecom_header_nav_resources_learning-center_events-webinars_click' },
        },
        resources: {
          release_notes: { click: 'gradlecom_header_nav_resources_resources_release-notes_click' },
          develocity_documentation: {
            click: 'gradlecom_header_nav_resources_resources_develocity-documentation_click',
          },
          api_documentation: { click: 'gradlecom_header_nav_resources_resources_api-documentation_click' },
          e_books_whitepapers: { click: 'gradlecom_header_nav_resources_resources_e-books-whitepapers_click' },
          dpe_newsletter: { click: 'gradlecom_header_nav_resources_resources_dpe-newsletter_click' },
          blog: { click: 'gradlecom_header_nav_resources_resources_blog_click' },
          youtube: { click: 'gradlecom_header_nav_resources_resources_youtube_click' },
        },
        oss_projects: {
          spring: { click: 'gradlecom_header_nav_resources_oss-projects_spring_click' },
          kotlin: { click: 'gradlecom_header_nav_resources_oss-projects_kotlin_click' },
          apache: { click: 'gradlecom_header_nav_resources_oss-projects_apache_click' },
          test_containers: { click: 'gradlecom_header_nav_resources_oss-projects_test-containers_click' },
          junit: { click: 'gradlecom_header_nav_resources_oss-projects_junit_click' },
          see_all: { click: 'gradlecom_header_nav_resources_oss-projects_see-all_click' },
        },
      },
      company: {
        about_us: {
          about_us: { click: 'gradlecom_header_nav_company_about-us_about-us_click' },
          our_story: { click: 'gradlecom_header_nav_company_about-us_our-story_click' },
          awards: { click: 'gradlecom_header_nav_company_about-us_awards_click' },
          our_team: { click: 'gradlecom_header_nav_company_about-us_our-team_click' },
          careers: { click: 'gradlecom_header_nav_company_about-us_careers_click' },
        },
        brand: {
          assets_guidelines: { click: 'gradlecom_header_nav_company_brand_assets-guidelines_click' },
          trademark_guidelines: { click: 'gradlecom_header_nav_company_brand_trademark-guidelines_click' },
        },
        press_media: { click: 'gradlecom_header_nav_company_press-media_click' },
      },
    },
  },
  newsletters: {
    index: {
      card: {
        click: 'gradlecom_newsletters_index_card_click',
      },
      subscribe: {
        submit: 'gradlecom_newsletters_index_subscribe_submit',
      },
      load_more: {
        click: 'gradlecom_newsletters_index_load-more_click',
      },
    },
  },
  brand: {
    logos: {
      download: { click: 'gradlecom_brand_logos_download_click' },
    },
    icons: {
      download: { click: 'gradlecom_brand_icons_download_click' },
    },
  },
  footer: {
    nav: {
      community: {
        x: { click: 'gradlecom_footer_nav_community_x_click' },
        blog: { click: 'gradlecom_footer_nav_community_blog_click' },
        linkedin: { click: 'gradlecom_footer_nav_community_linkedin_click' },
        youtube: { click: 'gradlecom_footer_nav_community_youtube_click' },
        newsletters: { click: 'gradlecom_footer_nav_community_newsletters_click' },
        gradledotorg: { click: 'gradlecom_footer_nav_community_gradledotorg_click' },
      },
      get_started: {
        solutions_overview: { click: 'gradlecom_footer_nav_get-started_solutions-overview_click' },
        develocity_demo: { click: 'gradlecom_footer_nav_get-started_develocity-demo_click' },
        request_trial: { click: 'gradlecom_footer_nav_get-started_request-trial_click' },
        contact: { click: 'gradlecom_footer_nav_get-started_contact_click' },
      },
      about: {
        about_us: { click: 'gradlecom_footer_nav_about_about-us_click' },
        careers: { click: 'gradlecom_footer_nav_about_careers_click' },
        brand: { click: 'gradlecom_footer_nav_about_brand_click' },
        press_media: { click: 'gradlecom_footer_nav_about_press-media_click' },
      },
      privacy_policy: { click: 'gradlecom_footer_nav_privacy-policy_click' },
      terms: { click: 'gradlecom_footer_nav_terms_click' },
      agreements: { click: 'gradlecom_footer_nav_agreements_click' },
      status: { click: 'gradlecom_footer_nav_status_click' },
    },
  },
  about: {
    nav: {
      link: { click: 'gradlecom_about_nav_link_click' },
    },
    team: {
      social: { click: 'gradlecom_about_team_social_click' },
    },
  },
  gradle: {
    close: {
      explore_develocity: { click: 'gradlecom_gradle_close_explore-develocity_click' },
    },
  },
  professionalServices: {
    cta: {
      consultation: { click: 'gradlecom_professional-services_cta_consultation_click' },
      pricing: { click: 'gradlecom_professional-services_cta_pricing_click' },
    },
  },
  supportPlans: {
    cta: {
      contact: { click: 'gradlecom_support-plans_cta_contact_click' },
      pricing: { click: 'gradlecom_support-plans_cta_pricing_click' },
      professional_services: { click: 'gradlecom_support-plans_cta_professional-services_click' },
    },
  },
  pricing: {
    // Hero CTAs (Part C): "Start free trial" → /trial/, "Contact us" → /contact/.
    // Wired in PricingHero via CtaPrimary/CtaSecondary `fathomEvent` (co-located with copy).
    hero: {
      trial: { click: 'gradlecom_pricing_hero_trial_click' },
      contact: { click: 'gradlecom_pricing_hero_contact_click' },
    },
    // Sidebar funnel cross-links to the pricing-extension pages (co-located with
    // their hrefs/labels in src/sections/pricing-v2/copy.ts EXTENSION_LINKS).
    funnel: {
      professional_services: { click: 'gradlecom_pricing_funnel_professional-services_click' },
      // support_plans returns here when the Support Plans page ships (it's currently
      // noindex/placeholder) — re-add alongside its EXTENSION_LINKS entry.
    },
  },
  // Per-product "Contact"/"Talk to us" CTA clicks on product/solution pages (Part B).
  // These CTAs point at `/contact/?product=<slug>` (deep-linked in resolve.ts) and render
  // through the NextLinkAdapter link seam, so the click is attributed by slug via
  // contactCtaFathomEvent(slug) — a whitelist lookup, never derived from copy. Keys are
  // derived from PRODUCT_INTEREST_NAMES (productCtaEvents), exactly like demo.cta. `default` covers a
  // contact CTA with no product slug (and the industry pages use `industries.*` instead).
  contact: {
    cta: productCtaEvents('contact'),
  },
  // Roadmap "Register your interest" CTA clicks. A deep-dive `doc` link can point at the
  // contact page for a pre-release/exploration capability (roadmap-chipped tabs, e.g. the
  // Docker / Universal Endpoint tabs on build-systems-package-managers). resolve.ts
  // (gradleDoc) deep-links those to `/contact/?product=<slug>&intent=roadmap`, so the
  // NextLinkAdapter link seam attributes the click to THIS event family — kept distinct
  // from `contact.cta.*` so genuine "Contact"/"Talk to us" CTAs and roadmap-interest
  // clicks don't collide. Keys are derived from PRODUCT_INTEREST_NAMES (productCtaEvents), exactly like
  // demo/trial/contact. `default` covers a roadmap link with no product slug. Resolve via
  // roadmapCtaFathomEvent(slug).
  roadmap: {
    cta: productCtaEvents('roadmap'),
  },
  // Industry-page CTAs (Part A). The soft "Talk to us" contact CTA + each lens-block
  // link render through a SLUG-AWARE link seam (makeIndustryLinkAdapter in adapters.tsx,
  // mounted in industry-page-view.tsx), which knows the page's industry slug. Events are
  // resolved by (industry slug, link href) via industryContactFathomEvent /
  // industryLensFathomEvent — structural lookups off the destination slug, never copy.
  // Lens keys mirror each spec's lens `to` targets (drift-guarded in
  // industry-cta-events.test.ts); regenerate when an industry spec's lens links change.
  industries: {
    'financial-services': {
      contact: { click: 'gradlecom_industries_financial-services_contact_cta_click' },
      lens: {
        'build-scan': { click: 'gradlecom_industries_financial-services_lens_build-scan_click' },
        'security-compliance': { click: 'gradlecom_industries_financial-services_lens_security-compliance_click' },
        'software-governance': { click: 'gradlecom_industries_financial-services_lens_software-governance_click' },
        'develocity-agents': { click: 'gradlecom_industries_financial-services_lens_develocity-agents_click' },
        'software-modernization': {
          click: 'gradlecom_industries_financial-services_lens_software-modernization_click',
        },
        'build-cache': { click: 'gradlecom_industries_financial-services_lens_build-cache_click' },
        'test-distribution': { click: 'gradlecom_industries_financial-services_lens_test-distribution_click' },
        efficiency: { click: 'gradlecom_industries_financial-services_lens_efficiency_click' },
        'flaky-tests-detection': { click: 'gradlecom_industries_financial-services_lens_flaky-tests-detection_click' },
        'failure-analytics': { click: 'gradlecom_industries_financial-services_lens_failure-analytics_click' },
        dora: { click: 'gradlecom_industries_financial-services_lens_dora_click' },
      },
    },
    fintech: {
      contact: { click: 'gradlecom_industries_fintech_contact_cta_click' },
      lens: {
        'build-cache': { click: 'gradlecom_industries_fintech_lens_build-cache_click' },
        'test-distribution': { click: 'gradlecom_industries_fintech_lens_test-distribution_click' },
        'predictive-test-selection': { click: 'gradlecom_industries_fintech_lens_predictive-test-selection_click' },
        efficiency: { click: 'gradlecom_industries_fintech_lens_efficiency_click' },
        'flaky-tests-detection': { click: 'gradlecom_industries_fintech_lens_flaky-tests-detection_click' },
        'failure-analytics': { click: 'gradlecom_industries_fintech_lens_failure-analytics_click' },
        dora: { click: 'gradlecom_industries_fintech_lens_dora_click' },
        'build-scan': { click: 'gradlecom_industries_fintech_lens_build-scan_click' },
        'security-compliance': { click: 'gradlecom_industries_fintech_lens_security-compliance_click' },
        'software-governance': { click: 'gradlecom_industries_fintech_lens_software-governance_click' },
      },
    },
    'manufacturing-logistics': {
      contact: { click: 'gradlecom_industries_manufacturing-logistics_contact_cta_click' },
      lens: {
        'flaky-tests-detection': {
          click: 'gradlecom_industries_manufacturing-logistics_lens_flaky-tests-detection_click',
        },
        'failure-analytics': { click: 'gradlecom_industries_manufacturing-logistics_lens_failure-analytics_click' },
        dora: { click: 'gradlecom_industries_manufacturing-logistics_lens_dora_click' },
        'build-cache': { click: 'gradlecom_industries_manufacturing-logistics_lens_build-cache_click' },
        'test-distribution': { click: 'gradlecom_industries_manufacturing-logistics_lens_test-distribution_click' },
        'build-systems-package-managers': {
          click: 'gradlecom_industries_manufacturing-logistics_lens_build-systems-package-managers_click',
        },
        efficiency: { click: 'gradlecom_industries_manufacturing-logistics_lens_efficiency_click' },
        'build-scan': { click: 'gradlecom_industries_manufacturing-logistics_lens_build-scan_click' },
        'develocity-agents': { click: 'gradlecom_industries_manufacturing-logistics_lens_develocity-agents_click' },
        'software-modernization': {
          click: 'gradlecom_industries_manufacturing-logistics_lens_software-modernization_click',
        },
      },
    },
    'media-entertainment': {
      contact: { click: 'gradlecom_industries_media-entertainment_contact_cta_click' },
      lens: {
        'flaky-tests-detection': {
          click: 'gradlecom_industries_media-entertainment_lens_flaky-tests-detection_click',
        },
        'failure-analytics': { click: 'gradlecom_industries_media-entertainment_lens_failure-analytics_click' },
        dora: { click: 'gradlecom_industries_media-entertainment_lens_dora_click' },
        'build-cache': { click: 'gradlecom_industries_media-entertainment_lens_build-cache_click' },
        'test-distribution': { click: 'gradlecom_industries_media-entertainment_lens_test-distribution_click' },
        'predictive-test-selection': {
          click: 'gradlecom_industries_media-entertainment_lens_predictive-test-selection_click',
        },
        efficiency: { click: 'gradlecom_industries_media-entertainment_lens_efficiency_click' },
        'build-scan': { click: 'gradlecom_industries_media-entertainment_lens_build-scan_click' },
        'develocity-agents': { click: 'gradlecom_industries_media-entertainment_lens_develocity-agents_click' },
        'software-modernization': {
          click: 'gradlecom_industries_media-entertainment_lens_software-modernization_click',
        },
      },
    },
    'retail-ecommerce': {
      contact: { click: 'gradlecom_industries_retail-ecommerce_contact_cta_click' },
      lens: {
        'flaky-tests-detection': { click: 'gradlecom_industries_retail-ecommerce_lens_flaky-tests-detection_click' },
        'failure-analytics': { click: 'gradlecom_industries_retail-ecommerce_lens_failure-analytics_click' },
        dora: { click: 'gradlecom_industries_retail-ecommerce_lens_dora_click' },
        'build-cache': { click: 'gradlecom_industries_retail-ecommerce_lens_build-cache_click' },
        'test-distribution': { click: 'gradlecom_industries_retail-ecommerce_lens_test-distribution_click' },
        'predictive-test-selection': {
          click: 'gradlecom_industries_retail-ecommerce_lens_predictive-test-selection_click',
        },
        efficiency: { click: 'gradlecom_industries_retail-ecommerce_lens_efficiency_click' },
        'build-scan': { click: 'gradlecom_industries_retail-ecommerce_lens_build-scan_click' },
        'develocity-agents': { click: 'gradlecom_industries_retail-ecommerce_lens_develocity-agents_click' },
        'software-modernization': { click: 'gradlecom_industries_retail-ecommerce_lens_software-modernization_click' },
      },
    },
    technology: {
      contact: { click: 'gradlecom_industries_technology_contact_cta_click' },
      lens: {
        'build-cache': { click: 'gradlecom_industries_technology_lens_build-cache_click' },
        'test-distribution': { click: 'gradlecom_industries_technology_lens_test-distribution_click' },
        'predictive-test-selection': { click: 'gradlecom_industries_technology_lens_predictive-test-selection_click' },
        efficiency: { click: 'gradlecom_industries_technology_lens_efficiency_click' },
        'develocity-analytics': { click: 'gradlecom_industries_technology_lens_develocity-analytics_click' },
        'failure-analytics': { click: 'gradlecom_industries_technology_lens_failure-analytics_click' },
        dora: { click: 'gradlecom_industries_technology_lens_dora_click' },
        'mcp-servers': { click: 'gradlecom_industries_technology_lens_mcp-servers_click' },
        'context-engineering-overview': {
          click: 'gradlecom_industries_technology_lens_context-engineering-overview_click',
        },
        ai: { click: 'gradlecom_industries_technology_lens_ai_click' },
      },
    },
  },
  // /events/github-universe-2026/ meeting booking (Google Sheet store). Funnel:
  // slot_select → submit (attempt) → success, with each server rejection keyed by the API's
  // error code — a structural lookup, never copy.
  githubUniverse2026: {
    hero: {
      book_cta: { click: 'gradlecom_gh-universe-2026_hero_book-cta_click' },
      agenda_cta: { click: 'gradlecom_gh-universe-2026_hero_agenda-cta_click' },
    },
    booking: {
      day: { select: 'gradlecom_gh-universe-2026_booking_day_select' },
      slot: { select: 'gradlecom_gh-universe-2026_booking_slot_select' },
      form: {
        submit: 'gradlecom_gh-universe-2026_booking_form_submit',
        success: 'gradlecom_gh-universe-2026_booking_form_success',
        error: {
          'slot-taken': 'gradlecom_gh-universe-2026_booking_form_error-slot-taken',
          'slot-closed': 'gradlecom_gh-universe-2026_booking_form_error-slot-closed',
          'already-booked': 'gradlecom_gh-universe-2026_booking_form_error-already-booked',
          'work-email-required': 'gradlecom_gh-universe-2026_booking_form_error-work-email-required',
          'verification-failed': 'gradlecom_gh-universe-2026_booking_form_error-verification-failed',
          invalid: 'gradlecom_gh-universe-2026_booking_form_error-invalid',
          'too-long': 'gradlecom_gh-universe-2026_booking_form_error-too-long',
          unavailable: 'gradlecom_gh-universe-2026_booking_form_error-unavailable',
        },
      },
    },
  },
  // Webinar template (src/sections/events/webinar), shared by every /events/<slug>/ page that
  // uses it — filter by page in Fathom for one webinar. submit = attempt (passed client
  // validation, posting to Pardot); success = back on the page with ?success=true after the
  // Pardot → /events/thankyou/ → Zoom round trip.
  eventsWebinar: {
    hero: {
      saveSeatLink: {
        click: 'gradlecom_events-webinar_hero_save-seat-link_click',
      },
    },
    register: {
      form: {
        submit: 'gradlecom_events-webinar_register_form_submit',
        success: 'gradlecom_events-webinar_register_form_success',
      },
    },
    // Past-event promo CTA (webinarPage.pastPromo), one key per promoted resource.
    pastPromo: {
      ai_native_sdlc: { click: 'gradlecom_events-webinar_past-promo_ai-native-sdlc_click' },
    },
  },
  // Per-product "Request a Demo" CTA clicks (#3426). Product/solution pages render
  // these CTAs via kraidle, so the click fires in the NextLinkAdapter link seam
  // (src/platform/pages/adapters.tsx) keyed by the `?product=<slug>` deep-link.
  // Keys are derived from PRODUCT_INTEREST_NAMES (the trusted slug whitelist) — the event is
  // looked up by slug, NEVER derived from copy. `default` covers a demo CTA with no
  // product slug. Use demoCtaFathomEvent(slug) to resolve.
  demo: {
    hero: {
      // Submit ATTEMPT (counts retries + server rejections).
      form_submitted: 'gradlecom_demo_hero_form_submitted',
      // Confirmed success screen — fires once. The true conversion.
      form_success: 'gradlecom_demo_hero_form_success',
      // Work-email gate monitoring (keep the gate, measure it). See README "Work-email gate".
      work_email: {
        // Client gate stopped a submit (fires on every blocked click).
        blocked: 'gradlecom_demo_hero_work-email_blocked',
        // Inline "use your work email" hint first rendered (once per ContactForm mount).
        warning_shown: 'gradlecom_demo_hero_work-email_warning-shown',
        // A submit that passed the gate after the hint was shown (once per mount).
        // warning-shown − this ≈ drop-off after seeing the hint.
        submitted_after_warning: 'gradlecom_demo_hero_work-email_submitted-after-warning',
        // "Run a free Build Scan" link shown after a blocked submit was clicked.
        build_scan_click: 'gradlecom_demo_hero_work-email_build-scan-click',
        // Server rejection card rendered (?error=work-email-required), keyed by the
        // server's `?reason=` — a structural lookup, never derived from copy.
        rejected: {
          free: 'gradlecom_demo_hero_work-email_rejected-free',
          disposable: 'gradlecom_demo_hero_work-email_rejected-disposable',
          invalid: 'gradlecom_demo_hero_work-email_rejected-invalid',
        },
      },
      // Form funnel + breakage monitoring (see README "Form funnel"). `started` = first real
      // input (not focus), once per form mount. `error` fires when the server's error card
      // renders, keyed by the `?error=` status — a structural lookup, never copy. `turnstile_error`
      // = the security check failed in the browser (once per mount, production hosts only).
      funnel: {
        started: 'gradlecom_demo_hero_form_started',
        turnstile_error: 'gradlecom_demo_hero_turnstile_error',
        error: {
          'verification-required': 'gradlecom_demo_hero_form_error-verification-required',
          'verification-failed': 'gradlecom_demo_hero_form_error-verification-failed',
          'submission-failed': 'gradlecom_demo_hero_form_error-submission-failed',
        },
      },
    },
    cta: productCtaEvents('demo'),
  },
} as const satisfies FathomEventStructure;

/** Shared lookup for the per-product CTA maps: the slug's own event, else `default`. */
function resolveProductCta(
  cta: Readonly<Record<string, { click: FathomEventName }>>,
  slug: string | undefined,
): FathomEventName {
  return slug && Object.hasOwn(cta, slug) ? cta[slug].click : cta.default.click;
}

/** Resolve the per-product demo-CTA click event from a (trusted) product slug.
 * Falls back to the generic `demo.cta.default` event for an unknown/absent slug —
 * a whitelist lookup, never a copy-derived name. Co-located with the events above. */
export function demoCtaFathomEvent(slug: string | undefined): FathomEventName {
  return resolveProductCta(FATHOM_EVENTS.demo.cta, slug);
}

/** Resolve the per-product trial-CTA click event from a (trusted) product slug.
 * Falls back to the generic `trial.cta.default` event for an unknown/absent slug —
 * a whitelist lookup, never a copy-derived name. Mirrors demoCtaFathomEvent. */
export function trialCtaFathomEvent(slug: string | undefined): FathomEventName {
  return resolveProductCta(FATHOM_EVENTS.trial.cta, slug);
}

/** Resolve the per-product `/scans/`-CTA click event from a (trusted) product slug.
 * The free Build Scan publish page (`/scans/`) is the destination of build-scan's primary
 * trial CTAs; resolve.ts deep-links a bare `/scans/` CTA on a known product page with
 * `?product=<slug>`. Falls back to the generic `scans.cta.default` event — a whitelist
 * lookup, never a copy-derived name. Mirrors trialCtaFathomEvent. */
export function scansCtaFathomEvent(slug: string | undefined): FathomEventName {
  return resolveProductCta(FATHOM_EVENTS.scans.cta, slug);
}

/** Resolve the per-product contact-CTA click event from a (trusted) product slug.
 * Falls back to the generic `contact.cta.default` event. Industry pages use the
 * industry-scoped resolvers below, NOT this one. Mirrors demoCtaFathomEvent. */
export function contactCtaFathomEvent(slug: string | undefined): FathomEventName {
  return resolveProductCta(FATHOM_EVENTS.contact.cta, slug);
}

/** Resolve the per-product roadmap ("Register your interest") CTA click event from a
 * (trusted) product slug. These are contact-page deep-links carrying `intent=roadmap`
 * (resolve.ts gradleDoc) — kept apart from contactCtaFathomEvent so roadmap-interest
 * clicks don't pollute the genuine contact-CTA events. Falls back to the generic
 * `roadmap.cta.default`. Mirrors demoCtaFathomEvent. */
export function roadmapCtaFathomEvent(slug: string | undefined): FathomEventName {
  return resolveProductCta(FATHOM_EVENTS.roadmap.cta, slug);
}

/** Resolve an industry page's soft contact ("Talk to us") CTA click event from its
 * (trusted, route-gated) industry slug. Returns null for an unknown slug so the seam
 * fires nothing rather than a wrong event. Co-located with FATHOM_EVENTS.industries. */
export function industryContactFathomEvent(industrySlug: string): FathomEventName | null {
  const map = FATHOM_EVENTS.industries as Record<string, { contact: { click: FathomEventName } }>;
  return Object.hasOwn(map, industrySlug) ? map[industrySlug].contact.click : null;
}

/** Resolve an industry lens-block link click event from (industry slug, destination
 * capability/solution slug). Both are structural — the destination slug is parsed from
 * the resolved `/product/<slug>/` or `/solutions/<slug>/` href, never from link copy.
 * Returns null when either is unmapped (e.g. an external/anchor lens link, or a spec
 * lens target with no event yet — the drift test flags the latter). */
export function industryLensFathomEvent(industrySlug: string, targetSlug: string): FathomEventName | null {
  const map = FATHOM_EVENTS.industries as Record<string, { lens: Record<string, { click: FathomEventName }> }>;
  if (!Object.hasOwn(map, industrySlug)) return null;
  const lens = map[industrySlug].lens;
  return Object.hasOwn(lens, targetSlug) ? lens[targetSlug].click : null;
}

// Legacy events - these should be migrated to the structured format above and not added to.
export const LEGACY_FATHOM_EVENTS = {
  TRIAL_REQUEST: 'gradlecom_trial-request',
  PRICING_REQUEST: 'gradlecom_pricing-request',
  NEWSLETTER_FORM: 'gradlecom_newsletter-form',
  MARKETING_BANNER_LINK_CLICK: 'gradlecom_marketing-banner-link-click',
} as const;

// Type helper to extract event names
type ExtractEventNames<T> = T extends string
  ? T
  : T extends Record<string, unknown>
    ? { [K in keyof T]: ExtractEventNames<T[K]> }[keyof T]
    : never;

// Export types for event names
export type FathomEventName = ExtractEventNames<typeof FATHOM_EVENTS>;
export type LegacyFathomEventName = (typeof LEGACY_FATHOM_EVENTS)[keyof typeof LEGACY_FATHOM_EVENTS];
export type AllFathomEventNames = FathomEventName | LegacyFathomEventName;
